import { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Gender,
  PaywallTypes,
  ProductInvoicePlacements,
  VideoState
} from '@/enums/apiEnums.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { routes } from '@/navigation/routes.tsx';
import Modal from '@/animations/Modal.tsx';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';
import EyeOffShape from '@/components/common/svg/shapes/EyeOffShape.tsx';
import { imageStyle } from './Content';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';

interface VideoSlideProps {
  videoUrl: string;
  posterUrl?: string;
  thumbhash?: string;
  candidateName: string;
  isActive: boolean;
  isDragging?: boolean;
  isPreview?: boolean;
  hideBackButton?: boolean;
}

const VideoSlide: FC<VideoSlideProps> = ({
  videoUrl,
  posterUrl,
  thumbhash,
  candidateName,
  isActive,
  isDragging,
  isPreview,
  hideBackButton = true
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const { aboutMe, premium } = useProfileStore();
  const isFemale = aboutMe?.profile?.gender === Gender.FEMALE;
  const isMale = aboutMe?.profile?.gender === Gender.MALE;
  const myHasVideo =
    (aboutMe?.profile?.v2videos?.length ?? 0) > 0 &&
    !aboutMe?.profile?.v2videos?.[0].in_review &&
    aboutMe?.profile?.v2videos?.[0].state === VideoState.STATE_ACTIVE;
  const isLocked =
    !isPreview &&
    ((isFemale && !myHasVideo && premium.isExpired) ||
      (isMale && premium.isExpired));

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive && !isDragging) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive, isDragging]);

  const handleAction = () => {
    if (isFemale) {
      navigate(routes.profileEdit);
    } else {
      setShowPaywall(true);
    }
  };

  return (
    <>
      {(thumbhash || posterUrl) && !videoReady && (
        <ImageWithLoader
          src={posterUrl}
          thumbhash={thumbhash}
          forceLoading={!isLocked && !videoReady}
          customLoader={isLocked ? <></> : undefined}
          alt=""
          style={{
            ...imageStyle,
            height: '100%',
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            ...(isLocked ? lockedVideoVisualStyle : {})
          }}
        />
      )}
      <div
        style={{
          ...imageStyle,
          height: '100%',
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          ...(isLocked ? lockedVideoVisualStyle : {})
        }}
      >
        <video
          ref={videoRef}
          src={videoUrl}
          poster={posterUrl}
          loop
          muted
          playsInline
          onCanPlay={() => setVideoReady(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: videoReady ? 1 : 0,
            transition: 'opacity 0.2s ease-out',
            display: 'block'
          }}
        />
      </div>
      {isLocked && (
        <div style={overlayStyle}>
          <EyeOffShape />
          <span style={titleStyle}>
            {t('discovery.video.title', { name: candidateName })}
          </span>
          <span style={subtitleTextStyle}>
            <Trans
              i18nKey={
                isFemale
                  ? 'discovery.video.subtitle.female'
                  : 'discovery.video.subtitle.male'
              }
              components={{
                br: <br />
              }}
            />
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction();
            }}
            style={buttonStyle}
          >
            {t(
              isFemale
                ? 'discovery.video.button.female'
                : 'discovery.video.button.male'
            )}
          </button>
        </div>
      )}

      <Modal
        fullScreen
        scrollable
        open={showPaywall}
        handleClose={() => setShowPaywall(false)}
      >
        <PaywallPopup
          handleClose={() => setShowPaywall(false)}
          hideBackButton={hideBackButton}
          type={PaywallTypes.TYPE_SUBSCRIPTION}
          placement={ProductInvoicePlacements.MATCHMAKER_PREMIUM_UPSALE_POPUP}
        />
      </Modal>
    </>
  );
};

const lockedVideoVisualStyle: CSSProperties = {
  filter: 'blur(20px)',
  transform: 'scale(1.1)',
  willChange: 'transform'
};

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 24px',
  background: 'rgba(0, 0, 0, 0.3)'
};

const titleStyle: CSSProperties = {
  color: '#ffffff',
  fontSize: '20px',
  fontWeight: 700,
  marginTop: '16px',
  textAlign: 'center'
};

const subtitleTextStyle: CSSProperties = {
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '12px',
  fontWeight: 510,
  marginTop: '6px',
  textAlign: 'center',
  lineHeight: '18px'
};

const buttonStyle: CSSProperties = {
  height: '40px',
  marginTop: '16px',
  padding: '10px 24px',
  borderRadius: '35px',
  border: 'none',
  backgroundColor: '#ffffff',
  color: '#000000',
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer'
};

export default VideoSlide;
